/**
 * Смена сессии и пользователя (CC §4.11, CLIENT_WEB.md §2.4 п. 5).
 *
 * Ключевое различие:
 *  - 401 `session_expired` — локальные данные, cursor и очередь pending
 *    СОХРАНЯЮТСЯ до определения `Me.user.id` новой сессии;
 *  - явный выход и смена владельца сессии — база удаляется целиком.
 */
import { api, authTransitionRequest, setApiSessionOwner, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import { clearMediaCacheStorage } from '@shared/media/cache'
import {
  clearPendingServerLogout,
  clearUserDeviceState,
  clearUserDbDeletion,
  closeUserDb,
  deleteUserDb,
  draftsRepo,
  getActiveUserId,
  markLogoutIntents,
  markUserDbDeletion,
  pendingServerLogout,
  pendingUserDbDeletions,
} from '@shared/db'
import { dropLocalPushSubscription, dropPushSubscription } from '@shared/push'
import { setSyncEngine, syncEngine } from '@shared/sync/instance'
import { clearLeadershipStateForUser, postTabMessage, releaseLeadership } from '@shared/ws/leader'

import { useDiscoveryStore } from '@features/discovery/discovery-store'
import { clearChannelCreateIntentForOwner } from '@features/channels/use-channels'

import { useAppStore } from '../model/app-store'
import { isBannedAppealSession } from '../model/account-access'
import { clearScreenScroll } from '../model/screen-scroll'
import { clearScrollMemory } from '../model/scroll-memory'
import { stopMeMirror } from './me-mirror'
import {
  fenceAuthTransition,
  withAuthTransition,
  withAuthTransitionOptions,
  type AuthTransitionGuard,
} from './auth-transition'

/** 401: показываем SYS-07, данные не трогаем. */
export async function handleSessionLost(expectedUserId?: string): Promise<void> {
  const store = useAppStore.getState()
  const userId = store.me?.user.id ?? null
  if (expectedUserId && userId !== expectedUserId) return

  // Fence сессии выполняется до любого await: поздний 401 от A
  // не должен после входа B остановить новый движок.
  syncEngine()?.stop()
  setSyncEngine(null)
  setApiSessionOwner(null)
  if (userId) postTabMessage({ type: 'session_lost', user_id: userId })
  releaseLeadership()
  const blocking = store.blocking
  if (
    blocking?.kind !== 'account_restricted' &&
    blocking?.kind !== 'account_suspended' &&
    blocking?.kind !== 'account_pending_deletion'
  ) {
    store.setBlocking({ kind: 'session_expired' })
  }

  // Черновики уже durable; best-effort flush не удерживает lifecycle fence.
  await flushDrafts()
}

/** 401/4401 в соседней вкладке: данные сохраняем, сетевые ресурсы отпускаем. */
export function handleRemoteSessionLost(expectedUserId: string): void {
  if (useAppStore.getState().me?.user.id !== expectedUserId) return
  stopMeMirror()
  syncEngine()?.stop()
  setSyncEngine(null)
  setApiSessionOwner(null)
  closeUserDb()
  releaseLeadership()
  const store = useAppStore.getState()
  store.setOnline(false)
  store.setBlocking({ kind: 'session_expired' })
}

/** Соседняя вкладка завершила/сменила сессию: немедленно отпускаем все ресурсы. */
export function handleRemoteSessionEnd(expectedUserId: string): void {
  if (useAppStore.getState().me?.user.id !== expectedUserId) return
  stopMeMirror()
  clearScrollMemory()
  clearScreenScroll()
  useDiscoveryStore.getState().reset()
  syncEngine()?.stop()
  setSyncEngine(null)
  setApiSessionOwner(null)
  closeUserDb()
  releaseLeadership()
  clearChannelCreateIntentForOwner(expectedUserId)
  void clearUserDeviceState(expectedUserId).catch(() => undefined)
  void clearMediaCache().catch(() => undefined)
  useAppStore.getState().reset()
}

/**
 * Active ban: stop every ordinary data channel but retain the authenticated
 * owner and local `/me` projection so the blocking appeal screen can operate.
 */
export function handleRestrictedAccountState(expectedUserId: string): void {
  const store = useAppStore.getState()
  if (store.me?.user.id !== expectedUserId) return

  stopMeMirror()
  syncEngine()?.stop()
  setSyncEngine(null)
  releaseLeadership()
  store.setOnline(false)
  store.setBlocking({ kind: 'account_restricted' })
  store.setPhase('ready')
}

type TerminalAccountBlocking = 'account_restricted' | 'account_suspended' | 'account_pending_deletion'
const terminalAccountRuns = new Map<string, Promise<void>>()

/** SYS-08/SYS-09: локальная privacy-cleanup как при logout, без server logout. */
export function handleTerminalAccountState(
  expectedUserId: string,
  blockingKind: TerminalAccountBlocking,
): void {
  const store = useAppStore.getState()
  if (store.me?.user.id && store.me.user.id !== expectedUserId) return

  // Приоритет фиксируется синхронно: следующий session.revoked не заменит
  // SYS-08/09 на общий SYS-07, пока идёт очистка.
  store.setOnline(false)
  store.setBlocking({ kind: blockingKind })
  // Web Lock/fallback lease может ожидаться. Локальная вкладка прекращает
  // session traffic и закрывает реплику сразу после terminal-сигнала.
  fenceAuthTransition()
  if (terminalAccountRuns.has(expectedUserId)) return

  const run = withAuthTransition(async (ensureOwned) => {
    clearChannelCreateIntentForOwner(expectedUserId)

    // Durable delete-intent ставится до первой внешней операции. Ошибку
    // нельзя проглатывать: без marker и без delete следующий boot потерял бы cleanup.
    const result = await deleteUserDbWithIntent(expectedUserId, ensureOwned)
    await ensureOwned()
    if (!result.deleted && result.blocked) {
      postTabMessage({ type: 'force_reload', user_id: expectedUserId })
    }

    // Пока terminal cleanup ждала origin lock, другой вход мог уже завершить
    // epoch B. БД A удалить безопасно, а общую browser PushSubscription B — нет.
    const activeUserId = await getActiveUserId()
    await ensureOwned()
    if (activeUserId && activeUserId !== expectedUserId) return

    // Ошибка fail-closed push barrier останавливает переход: отзывать cookie
    // или разрешать следующего owner с общим endpoint после неё нельзя.
    await dropPushSubscription({ authTransition: true })
    await ensureOwned()
    // Атомарная очистка сама повторно сверяет owner в device DB.
    await bestEffort(() => clearUserDeviceState(expectedUserId))
    await ensureOwned()
    await bestEffort(clearMediaCache)
    await ensureOwned()

    // Поздняя cleanup A не сбрасывает уже вошедшего B.
    if (useAppStore.getState().me?.user.id === expectedUserId) {
      useAppStore.getState().reset()
      useAppStore.getState().setBlocking({ kind: blockingKind })
    }
  })
    .catch(() => {
      if (useAppStore.getState().me?.user.id === expectedUserId) {
        useAppStore.getState().setBlocking({ kind: blockingKind })
      }
    })
    .finally(() => {
      if (terminalAccountRuns.get(expectedUserId) === run) {
        terminalAccountRuns.delete(expectedUserId)
      }
    })
  terminalAccountRuns.set(expectedUserId, run)
}

async function flushDrafts(): Promise<void> {
  try {
    await draftsRepo.all()
  } catch {
    /* база могла быть уже закрыта */
  }
}

const LOGOUT_STEP_TIMEOUT_MS = 2_000
const LOGOUT_STEP_TIMED_OUT = Symbol('logout step timed out')

async function waitForLogoutStep<T>(task: () => T | Promise<T>): Promise<T | typeof LOGOUT_STEP_TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<typeof LOGOUT_STEP_TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(LOGOUT_STEP_TIMED_OUT), LOGOUT_STEP_TIMEOUT_MS)
  })

  try {
    return await Promise.race([Promise.resolve().then(task), deadline])
  } finally {
    clearTimeout(timer)
  }
}

async function bestEffort(task: () => unknown | Promise<unknown>): Promise<void> {
  try {
    await task()
  } catch {
    /* Каждый шаг очистки независим и не должен блокировать следующий. */
  }
}

function bestEffortNow(task: () => unknown): void {
  try {
    task()
  } catch {
    /* Synchronous lifecycle fence продолжает освобождать остальные ресурсы. */
  }
}

const userDbDeletionRuns = new Map<string, Promise<{ deleted: boolean; blocked: boolean }>>()

async function deleteUserDbWithIntent(
  userId: string,
  ensureOwned: AuthTransitionGuard,
): Promise<{ deleted: boolean; blocked: boolean }> {
  const existing = userDbDeletionRuns.get(userId)
  if (existing) return existing

  const run = (async () => {
    let markerError: unknown
    try {
      await markUserDbDeletion(userId)
    } catch (error) {
      markerError = error
    }
    // Marker мог ждать IDB дольше fallback lease. Поздний
    // deleteDatabase иначе удалил бы уже свежую same-user реплику.
    await ensureOwned()
    const result = await deleteUserDb(userId)
    await ensureOwned()
    if (result.deleted) {
      // Ошибку clear не глотаем: иначе stale marker после same-user relogin
      // удалил бы уже свежую БД при следующем pageshow.
      await clearUserDbDeletion(userId)
    } else if (markerError) {
      // Без durable intent и без фактического удаления продолжать lifecycle
      // нельзя: после crash старая переписка осталась бы навсегда.
      throw markerError
    }
    return result
  })().finally(() => userDbDeletionRuns.delete(userId))
  userDbDeletionRuns.set(userId, run)
  return run
}

/**
 * До открытия новой БД того же user устраняет даже stale intent старой epoch.
 * Второй cross-tab delete ждёт более ранний запрос в IndexedDB connection queue;
 * marker снимается лишь после `success` этого барьера, поэтому late delete не
 * может удалить уже открытую свежую реплику.
 */
export async function resolvePendingDeletionBeforeSession(
  userId: string,
  ensureOwned: AuthTransitionGuard,
): Promise<void> {
  if (!(await pendingUserDbDeletions()).includes(userId)) return
  await ensureOwned()
  const result = await deleteUserDbWithIntent(userId, ensureOwned)
  await ensureOwned()
  if (!result.deleted) throw new Error('user database deletion is blocked')
}

/** До startup повторяет privacy-delete, который был blocked замороженной вкладкой. */
export async function recoverPendingUserDbDeletions(ensureOwned: AuthTransitionGuard): Promise<boolean> {
  const pending = await pendingUserDbDeletions()
  await ensureOwned()
  let complete = true
  for (const userId of pending) {
    await ensureOwned()
    if (useAppStore.getState().me?.user.id === userId) handleRemoteSessionEnd(userId)
    const result = await deleteUserDbWithIntent(userId, ensureOwned)
    await ensureOwned()
    if (!result.deleted) complete = false
  }
  return complete
}

/**
 * Offline logout уже удалил локальные данные, но cookie могла остаться.
 * Перед новым `/me` под auth mutex сверяем владельца: marker A никогда не
 * отзывает успевшую появиться сессию B.
 */
export async function recoverPendingServerLogout(ensureOwned: AuthTransitionGuard): Promise<boolean> {
  const expectedUserId = await pendingServerLogout()
  await ensureOwned()
  if (!expectedUserId) return true

  let actualUserId: string | null = null
  try {
    await ensureOwned()
    actualUserId = unwrap(await api.GET('/me', authTransitionRequest({ suppressGlobalErrors: true }))).user.id
    await ensureOwned()
  } catch (error) {
    await ensureOwned()
    if (
      error instanceof ApiError &&
      (error.code === 'unauthenticated' ||
        error.code === 'session_expired' ||
        error.code === 'account_restricted' ||
        error.code === 'account_suspended' ||
        error.code === 'account_pending_deletion')
    ) {
      await ensureOwned()
      await clearPendingServerLogout(expectedUserId)
      await ensureOwned()
      return true
    }
    return false
  }

  if (actualUserId !== expectedUserId) {
    await ensureOwned()
    await clearPendingServerLogout(expectedUserId)
    await ensureOwned()
    return true
  }

  try {
    await ensureOwned()
    await dropPushSubscription({ authTransition: true, suppressGlobalErrors: true })
    await ensureOwned()
    await api.POST('/auth/logout', authTransitionRequest({ suppressGlobalErrors: true }))
    await ensureOwned()
    await clearPendingServerLogout(expectedUserId)
    await ensureOwned()
    return true
  } catch {
    // Потерю lease не превращаем в обычный offline-result.
    await ensureOwned()
    return false
  }
}

/** Возврат из freeze/bfcache доводит пропущенный remote logout до удаления БД. */
export function startPendingDeletionRecovery(
  reloadPage: () => void = () => globalThis.location.reload(),
): () => void {
  let active: Promise<void> | null = null
  const recover = (): void => {
    if (document.visibilityState === 'hidden') return
    if (active) return
    const ownerBefore = useAppStore.getState().me?.user.id ?? null
    let complete = false
    active = (async () => {
      // iOS toggles visibility while WebDriver/native UI takes a screenshot.
      // Avoid creating an origin mutex at all when there is no durable work:
      // a navigation can freeze that otherwise empty transition before its
      // asynchronous IndexedDB release reaches `finally`.
      if (!(await pendingUserDbDeletions()).length) {
        complete = true
        return
      }
      await withAuthTransitionOptions(
        async (ensureOwned) => {
          complete = await recoverPendingUserDbDeletions(ensureOwned)
        },
        { fenceOtherTabs: false, diagnosticName: 'foreground-delete-recovery' },
      )
    })()
      .then(() => {
        // Frozen A могла пропустить owner_changed/login B. После durable
        // cleanup перезапускаем bootstrap, чтобы authoritative cookie owner
        // открыл правильную сессию вместо вечного unauthenticated UI.
        if (complete && ownerBefore && useAppStore.getState().me?.user.id !== ownerBefore) {
          reloadPage()
        }
      })
      .catch(() => undefined)
      .finally(() => {
        active = null
      })
  }
  globalThis.addEventListener('pageshow', recover)
  document.addEventListener('visibilitychange', recover)
  return () => {
    globalThis.removeEventListener('pageshow', recover)
    document.removeEventListener('visibilitychange', recover)
  }
}

async function ownsCurrentServerSession(userId: string, ensureOwned: AuthTransitionGuard): Promise<boolean> {
  // `active_user_id` — лишь локальный hint и может остаться A после crash
  // между verify B и commit owner. Destructive logout всегда сверяем с
  // authoritative cookie-session под origin-wide auth lock.
  try {
    await ensureOwned()
    const response = await api.GET('/me', authTransitionRequest())
    await ensureOwned()
    return unwrap(response).user.id === userId
  } catch (error) {
    await ensureOwned()
    if (error instanceof ApiError && (error.code === 'unauthenticated' || error.code === 'session_expired')) {
      return false
    }
    throw error
  }
}

/** Отличает отказ до durable intent от ошибки уже запущенной очистки. */
class LogoutPreflightError extends Error {
  override readonly cause: unknown

  constructor(cause: unknown) {
    super('logout durable preflight failed')
    this.name = lifecycleErrorName(cause, 'LogoutPreflightError')
    this.cause = cause
  }
}

/** Durable intent есть, но lifecycle прерван до запуска cleanup. */
class LogoutPreparedTransitionError extends Error {
  override readonly cause: unknown

  constructor(cause: unknown) {
    super('logout interrupted after durable preflight')
    this.name = lifecycleErrorName(cause, 'LogoutPreparedTransitionError')
    this.cause = cause
  }
}

function lifecycleErrorName(cause: unknown, fallback: string): string {
  return typeof cause === 'object' && cause !== null && 'name' in cause && typeof cause.name === 'string'
    ? cause.name
    : fallback
}

/** Явный выход (S-24): удаляем локальные данные безусловно. */
export async function logout(): Promise<void> {
  let preparedUserId: string | null = null
  let prepared = false
  let transitionStarted = false
  try {
    await withAuthTransitionOptions(
      (ensureOwned) => {
        transitionStarted = true
        return logoutExclusively(preparedUserId, ensureOwned)
      },
      {
        fenceOtherTabs: true,
        diagnosticName: 'logout',
        prepareBeforeFence: async (ensureOwned) => {
          preparedUserId = await prepareLogoutIntents(ensureOwned)
          prepared = true
        },
      },
    )
  } catch (error) {
    if (!prepared) throw new LogoutPreflightError(error)
    if (!transitionStarted) throw new LogoutPreparedTransitionError(error)
    throw error
  }
}

async function prepareLogoutIntents(ensureOwned: AuthTransitionGuard): Promise<string | null> {
  const store = useAppStore.getState()
  let userId = store.me?.user.id ?? null

  if (!userId) {
    try {
      const storedUserId = await waitForLogoutStep(getActiveUserId)
      await ensureOwned()
      if (storedUserId !== LOGOUT_STEP_TIMED_OUT) userId = storedUserId
    } catch {
      /* store уже содержит владельца при штатном выходе */
    }
  }

  // IDB может быть недоступна ровно на explicit logout. Тогда owner
  // перепроверяем у cookie-сессии: нельзя показывать успешный выход,
  // оставив рабочую cookie, которая сразу войдёт обратно после reload.
  if (!userId) {
    try {
      await ensureOwned()
      const response = await api.GET('/me', authTransitionRequest())
      await ensureOwned()
      userId = unwrap(response).user.id
    } catch (error) {
      await ensureOwned()
      if (
        !(error instanceof ApiError) ||
        (error.code !== 'unauthenticated' && error.code !== 'session_expired')
      ) {
        store.setBlocking({ kind: 'diagnostic', code: 'unavailable', requestId: '' })
        throw error
      }
    }
  }

  if (userId) {
    await ensureOwned()
    try {
      // Пишем server-revoke и privacy-delete атомарно, пока текущая
      // user DB и UI ещё активны. Отказ IDB не превращается в
      // «успешный» выход с живой cookie.
      await markLogoutIntents(userId)
    } catch (markerError) {
      await ensureOwned()
      store.setBlocking({ kind: 'diagnostic', code: 'unavailable', requestId: '' })
      throw markerError
    }
  }

  return userId
}

async function logoutExclusively(userId: string | null, ensureOwned: AuthTransitionGuard): Promise<void> {
  const store = useAppStore.getState()

  let ownsOriginSession = false
  let ownerProofFailed = false
  try {
    if (userId) ownsOriginSession = await ownsCurrentServerSession(userId, ensureOwned)
  } catch {
    // S-24 разрешает локальный logout без сети. Durable marker не даст
    // следующему bootstrap принять ещё живую cookie как успешный вход.
    ownerProofFailed = true
  }
  await ensureOwned()

  /*
   * §8.2 п. 6: подписка удаляется ДО server logout. Auth-transition lock
   * не пускает вход B между этими шагами.
   */
  if (ownsOriginSession) {
    // Ban application already revoked the previous sessions and their push
    // subscriptions; the fresh appeal-only session never registers one. Its
    // server allowlist intentionally has no push endpoint, so logout may proceed
    // directly without weakening the normal fail-closed push barrier.
    let pushCleanupReady = isBannedAppealSession(store.me)
    if (!pushCleanupReady) {
      try {
        await dropPushSubscription({ authTransition: true })
        pushCleanupReady = true
      } catch {
        // Fail-closed marker не записался: cookie сохраняем, durable logout
        // intent повторит весь порядок на следующем bootstrap.
      }
    }
    await ensureOwned()
    if (pushCleanupReady) {
      let serverLogoutConfirmed = false
      try {
        await ensureOwned()
        await api.POST('/auth/logout', authTransitionRequest())
        serverLogoutConfirmed = true
      } catch {
        // Локальная privacy-cleanup обязательна даже при 503/offline. Marker
        // остаётся и будет повторён до `/me` на следующем bootstrap.
      }
      await ensureOwned()
      if (serverLogoutConfirmed && userId) {
        await clearPendingServerLogout(userId)
        await ensureOwned()
      }
    }
  } else if (userId && !ownerProofFailed) {
    // Cookie уже принадлежит другой epoch или сессии нет: A marker устарел.
    await clearPendingServerLogout(userId)
    await ensureOwned()
  }

  // Теперь server session либо отозвана, либо уже принадлежит B.
  // Поэтому synchronous fence не может задеть новую auth epoch.
  await ensureOwned()
  bestEffortNow(stopMeMirror)
  bestEffortNow(clearScrollMemory)
  bestEffortNow(clearScreenScroll)
  bestEffortNow(() => useDiscoveryStore.getState().reset())
  bestEffortNow(() => syncEngine()?.stop())
  bestEffortNow(() => setSyncEngine(null))
  setApiSessionOwner(null)
  if (userId) bestEffortNow(() => postTabMessage({ type: 'logout', user_id: userId }))
  bestEffortNow(closeUserDb)
  bestEffortNow(releaseLeadership)
  bestEffortNow(() => store.reset())
  await bestEffort(clearMediaCache)
  await ensureOwned()

  if (userId) {
    clearChannelCreateIntentForOwner(userId)
    clearLeadershipStateForUser(userId)
    await bestEffort(async () => {
      const result = await deleteUserDbWithIntent(userId, ensureOwned)
      if (result.deleted) await clearUserDbDeletion(userId).catch(() => undefined)
      // Если удаление заблокировано замороженной вкладкой — просим перезагрузку.
      if (!result.deleted && result.blocked) {
        await bestEffort(() => postTabMessage({ type: 'force_reload', user_id: userId }))
      }
    })
    await ensureOwned()
  }

  // Транзакция сама не стирает active_user другого owner.
  if (userId) {
    await ensureOwned()
    await bestEffort(() => clearUserDeviceState(userId))
    await ensureOwned()
  }
}

/** Навигация выполняется только после подтверждённого logout. */
export async function settleLogoutFlow(
  logoutAction: () => void | Promise<void>,
  navigateToStart: () => void | Promise<void>,
): Promise<void> {
  try {
    await logoutAction()
  } catch (error) {
    // Preflight не смог durable зафиксировать logout: UI и owner
    // остаются активны, поэтому переход на start screen запрещён.
    if (error instanceof LogoutPreflightError || error instanceof LogoutPreparedTransitionError) return
    // После durable preflight локальная очистка могла уже завершиться.
    // В этом случае start screen остаётся консистентным с очищенным store.
  }

  try {
    await navigateToStart()
  } catch {
    /* вызывающий намеренно запускает поток без ожидания; rejection уже обработан */
  }
}

export function logoutAndNavigate(navigateToStart: () => void | Promise<void>): Promise<void> {
  return settleLogoutFlow(logout, navigateToStart)
}

/** §9.3 п. 8: при выходе и смене пользователя кэш медиа удаляется. */
async function clearMediaCache(): Promise<void> {
  await clearMediaCacheStorage()
}

/**
 * Смена владельца сессии (CC §4.11 п. 2): вошли под другим `Me.user.id`.
 * База предыдущего пользователя удаляется целиком, без выборочной чистки.
 */
export async function handleOwnerChange(
  previousUserId: string,
  nextUserId: string,
  ensureOwned: AuthTransitionGuard,
): Promise<void> {
  await ensureOwned()
  await markUserDbDeletion(previousUserId)
  await ensureOwned()
  postTabMessage({ type: 'owner_changed', previous_user_id: previousUserId, next_user_id: nextUserId })

  stopMeMirror()
  clearScrollMemory()
  clearScreenScroll()
  useDiscoveryStore.getState().reset()
  syncEngine()?.stop()
  setSyncEngine(null)
  setApiSessionOwner(null)
  closeUserDb()
  releaseLeadership()
  clearLeadershipStateForUser(previousUserId)
  clearChannelCreateIntentForOwner(previousUserId)

  // Cookie уже принадлежит B; server DELETE A невозможен. Browser endpoint
  // обязательно снимаем до `syncPushSubscription(B)`, чтобы A/B не делили его.
  await ensureOwned()
  await dropLocalPushSubscription()
  await ensureOwned()

  const deletion = await deleteUserDbWithIntent(previousUserId, ensureOwned)
  await ensureOwned()
  if (!deletion.deleted) {
    if (deletion.blocked) {
      postTabMessage({ type: 'force_reload', user_id: previousUserId })
    }
    throw new Error('previous user database deletion is blocked')
  }
  await clearUserDeviceState(previousUserId)
  await ensureOwned()
  await clearMediaCache()
  await ensureOwned()
}
